import {Duration, DurationOptions, ToISOTimeDurationOptions} from "luxon";

export type HelperFormats = "hh:mm" | "hh:mm:ss" | "yyyy-MM-dd HH:mm:ss" | "yyyy-MM-dd";

export type FormatOut = string | HelperFormats | "ISO";

export type DurationType = "years" | "months" | "days" | "hours" | "minutes" | "seconds" | "milliseconds";
export type DurationObj = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

export type DurationProp = DurationObj | string;

export class RRDuration {
  private _duration: Duration;

  constructor(from: DurationProp) {
    if (typeof from !== "string") this._duration = Duration.fromObject(from);
    else this._duration = Duration.fromISOTime(from);
    if (!this._duration.isValid) throw new Error("RRDuration is incorrectly constructed, can't be constructed from: " + JSON.stringify(from));
  }

  static fromISOTime(text: string, opts?: DurationOptions): RRDuration {
    const {years, months, days, hours, minutes, seconds, milliseconds} = Duration.fromISOTime(text, opts);
    return new RRDuration({years, months, days, hours, minutes, seconds, milliseconds});
  }

  toISOTime(opts?: ToISOTimeDurationOptions): string {
    return this._duration.toISOTime(opts);
  }

  toMillis(): number {
    return this._duration.toMillis();
  }

  /**
   * @returns the duration in seconds
   */
  toSeconds(): number {
    return this.toMillis() / 1000;
  }

  /**
   * @returns the duration in minutes
   */
  toMinutes(): number {
    return this.toSeconds() / 60;
  }

  /**
   * @param duration - to substract from this duration
   * @returns this RRDuration MUTATED
   */
  minus(duration: RRDuration | DurationObj): RRDuration {
    let result = this.toString("ISO");
    if (duration instanceof RRDuration) {
      result = this._duration.minus(duration.toLuxon).toISOTime();
    } else {
      result = this._duration.minus(duration).toISOTime();
    }
    return new RRDuration(result);
  }

  /**
   * @param duration - to add to this duration
   * @returns this RRDuration MUTATED
   */
  plus(duration: RRDuration | DurationObj): RRDuration {
    let result = this.toString("ISO");
    if (duration instanceof RRDuration) {
      result = this._duration.plus(duration.toLuxon).toISOTime();
    } else {
      result = this._duration.plus(duration).toISOTime();
    }
    return new RRDuration(result);
  }

  compare(duration: RRDuration): 0 | 1 | -1 {
    const milis1 = this._duration.toMillis();
    const milis2 = duration._duration.toMillis();
    if (milis1 > milis2) return 1;
    else if (milis1 < milis2) return -1;
    return 0;
  }

  private get toLuxon(): Duration {
    return Duration.fromISOTime(this.toString("ISO"));
  }

  /**
   * "S" for milliseconds
   * "s" for seconds
   * "m" for minutes
   * "h" for hours
   * "d" for days
   * "M" for months
   * "y" for years
   * "ISO" for ISO 8601 formatted string
   * @param outputFormat -
   *
   */
  toString(outputFormat: FormatOut): string {
    if (outputFormat === "ISO") {
      return this._duration.toISOTime();
    }
    return this._duration.toFormat(outputFormat);
  }

  get milliseconds(): number {
    return this._duration.milliseconds;
  }

  get seconds(): number {
    return this._duration.seconds;
  }

  get minutes(): number {
    return this._duration.minutes;
  }

  get hours(): number {
    return this._duration.hours;
  }

  get days(): number {
    return this._duration.days;
  }

  get months(): number {
    return this._duration.months;
  }

  get years(): number {
    return this._duration.years;
  }

  suppress(values: DurationType[]): RRDuration {
    values.forEach((unit) => {
      if (unit === "milliseconds") this._duration = this._duration.minus({millisecond: this._duration.milliseconds});
      if (unit === "seconds") this._duration = this._duration.minus({second: this._duration.seconds});
      if (unit === "minutes") this._duration = this._duration.minus({minute: this._duration.minutes});
      if (unit === "hours") this._duration = this._duration.minus({hour: this._duration.hours});
      if (unit === "days") this._duration = this._duration.minus({day: this._duration.days});
      if (unit === "months") this._duration = this._duration.minus({month: this._duration.months});
      if (unit === "years") this._duration = this._duration.minus({year: this._duration.years});
    });
    const {years, months, days, hours, minutes, seconds, milliseconds} = this._duration;
    return new RRDuration({years, months, days, hours, minutes, seconds, milliseconds});
  }
}
